import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import StarRating from "@/components/StarRating";
import { ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function ReviewSection({ skuMadre, tenantSlug }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ rating: 5, title: "", body: "" });
  const [busy, setBusy] = useState(false);

  const refresh = () => axios.get(`${API}/public/reviews/${skuMadre}?tenant_slug=${tenantSlug}`).then((r) => setData(r.data));
  useEffect(() => { refresh(); }, [skuMadre, tenantSlug]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/reviews", { sku_madre: skuMadre, tenant_slug: tenantSlug, ...form });
      toast.success("Recensione pubblicata!");
      setForm({ rating: 5, title: "", body: "" });
      refresh();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  return (
    <section className="border-t border-border pt-10 mt-10" data-testid="reviews-section">
      <div className="flex items-end justify-between mb-6">
        <h2 className="font-heading font-bold text-2xl">Recensioni clienti</h2>
        {data && data.count > 0 && (
          <div className="flex items-center gap-2">
            <StarRating value={data.average} size="md" testid="avg-stars" />
            <span className="text-sm text-muted-foreground"><b className="text-foreground">{data.average}</b> / 5 · {data.count} recensioni</span>
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-4">
          {(data?.items || []).length === 0 && <p className="text-sm text-muted-foreground">Nessuna recensione ancora. Sii il primo!</p>}
          {(data?.items || []).map((r) => (
            <div key={r.id} className="border border-border rounded-sm p-4" data-testid={`review-${r.id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="flex items-center gap-2">
                  <StarRating value={r.rating} testid={`review-stars-${r.id}`} />
                  {r.verified_purchase && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded-full">
                      <ShieldCheck className="w-3 h-3" /> Acquisto verificato
                    </span>
                  )}
                </div>
                <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
              </div>
              <div className="font-medium text-sm">{r.title}</div>
              <div className="text-xs text-muted-foreground mb-2">{r.customer_name}</div>
              <p className="text-sm text-foreground whitespace-pre-line">{r.body}</p>
            </div>
          ))}
        </div>

        <div>
          {user?.role === "customer" ? (
            <form onSubmit={submit} className="bg-white border border-border rounded-sm p-5 space-y-3" data-testid="review-form">
              <div className="font-heading font-bold text-lg">Lascia una recensione</div>
              <div>
                <label className="text-xs font-medium text-muted-foreground mb-1 block">Voto</label>
                <StarRating value={form.rating} size="lg" onChange={(v) => setForm({ ...form, rating: v })} testid="form-stars" />
              </div>
              <input data-testid="review-title" required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Titolo" className="w-full h-10 px-3 rounded-sm border border-border text-sm" />
              <textarea data-testid="review-body" required value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })}
                placeholder="La tua esperienza…" rows={4} className="w-full px-3 py-2 rounded-sm border border-border text-sm" />
              <button data-testid="review-submit" disabled={busy} type="submit"
                className="w-full h-10 rounded-sm bg-brand text-white text-sm font-medium hover:bg-brand-hover disabled:opacity-50">
                {busy ? "..." : "Pubblica recensione"}
              </button>
            </form>
          ) : (
            <div className="bg-accent/30 border border-border rounded-sm p-5 text-sm text-muted-foreground">
              Vuoi lasciare una recensione? <Link to="/login" className="text-brand hover:underline">Accedi</Link> oppure <Link to="/register" className="text-brand hover:underline">registrati</Link>.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
